"use client";

import { c as _c } from "react/compiler-runtime";
import { ANYSPEND_MAINNET_BASE_URL } from "../../../anyspend/constants/index.js";
import { useAccountWallet } from "../../../global-account/react/index.js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
function buildWalletAuthMessage(walletAddress, timestamp) {
  return `AnySpend wants to verify your identity for card payments.\n\nThis signature does not trigger a blockchain transaction or cost any gas.\n\nWallet: ${walletAddress.toLowerCase()}\nNonce: ${timestamp}`;
}
/** Module-level signature cache to avoid repeated wallet prompts within the 5-minute window. */
const headerCache = new Map();
/**
 * Returns cached wallet auth headers without triggering a wallet signature prompt.
 * Returns undefined if no valid cache exists for the given address.
 */
export function getCachedWalletHeaders(address) {
  const cached = headerCache.get(address.toLowerCase());
  if (cached && Date.now() < cached.expiresAt) return cached.headers;
  return undefined;
}
/**
 * Returns a function that builds the wallet-signature auth headers.
 * Uses useAccountWallet (thirdweb) instead of wagmi so signing works
 * for all wallet types (social login, EOA, smart wallet, etc.).
 * Caches signatures for 4 minutes (server allows 5-minute window).
 */
export function useWalletAuthHeaders() {
  const $ = _c(6);
  const {
    address,
    wallet
  } = useAccountWallet();
  const signMessage = wallet.signMessage;
  let t0;
  if ($[0] !== address || $[1] !== signMessage) {
    t0 = async () => {
      if (!address) {
        throw new Error("No wallet connected");
      }
      if (!signMessage) {
        throw new Error("Wallet does not support message signing");
      }
      const walletAddress = address.toLowerCase();
      const cached = headerCache.get(walletAddress);
      if (cached && Date.now() < cached.expiresAt) {
        return cached.headers;
      }
      const timestamp = Math.floor(Date.now() / 1000);
      const message = buildWalletAuthMessage(walletAddress, timestamp);
      const signature = await signMessage({
        message
      });
      const headers = {
        "X-Wallet-Address": walletAddress,
        "X-Wallet-Signature": signature,
        "X-Wallet-Timestamp": String(timestamp)
      };
      headerCache.set(walletAddress, {
        headers,
        expiresAt: Date.now() + 240000
      });
      return headers;
    };
    $[0] = address;
    $[1] = signMessage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const getHeaders = t0;
  let t1;
  if ($[3] !== address || $[4] !== getHeaders) {
    t1 = {
      address,
      getHeaders
    };
    $[3] = address;
    $[4] = getHeaders;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useKycStatus(t0) {
  const $ = _c(13);
  const enabled = t0 === undefined ? true : t0;
  const {
    address,
    getHeaders
  } = useWalletAuthHeaders();
  let t1;
  if ($[0] !== address) {
    t1 = ["kyc-status", address];
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== getHeaders) {
    t2 = async () => {
      const headers = await getHeaders();
      const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/kyc/status`, {
        headers
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || "Failed to fetch KYC status");
      }
      return json.data;
    };
    $[2] = getHeaders;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = enabled && !!address;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 30000,
      retry: 0,
      refetchOnWindowFocus: false
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(t4);
  const t5 = data || null;
  let t6;
  if ($[8] !== error || $[9] !== isLoading || $[10] !== refetch || $[11] !== t5) {
    t6 = {
      kycStatus: t5,
      isLoadingKycStatus: isLoading,
      kycStatusError: error,
      refetchKycStatus: refetch
    };
    $[8] = error;
    $[9] = isLoading;
    $[10] = refetch;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
export function useCreateKycInquiry() {
  const $ = _c(6);
  const {
    address,
    getHeaders
  } = useWalletAuthHeaders();
  let t0;
  if ($[0] !== address || $[1] !== getHeaders) {
    t0 = {
      mutationFn: async () => {
        if (!address) {
          throw new Error("No wallet connected");
        }
        const headers = await getHeaders();
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/kyc/inquiry`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers
          }
        });
        const json = await response.json();
        if (!response.ok) {
          throw new Error(json.message || "Failed to create KYC inquiry");
        }
        return json.data;
      }
    };
    $[0] = address;
    $[1] = getHeaders;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    mutateAsync,
    isPending
  } = useMutation(t0);
  let t1;
  if ($[3] !== isPending || $[4] !== mutateAsync) {
    t1 = {
      createInquiry: mutateAsync,
      isCreatingInquiry: isPending
    };
    $[3] = isPending;
    $[4] = mutateAsync;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useVerifyKyc() {
  const $ = _c(6);
  const {
    address,
    getHeaders
  } = useWalletAuthHeaders();
  let t0;
  if ($[0] !== address || $[1] !== getHeaders) {
    t0 = {
      mutationFn: async inquiryId => {
        if (!address) {
          throw new Error("No wallet connected");
        }
        const headers = await getHeaders();
        const response = await fetch(`${ANYSPEND_MAINNET_BASE_URL}/kyc/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers
          },
          body: JSON.stringify({
            inquiryId
          })
        });
        const json = await response.json();
        if (!response.ok) {
          throw new Error(json.message || "Failed to verify KYC");
        }
        return json.data;
      }
    };
    $[0] = address;
    $[1] = getHeaders;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    mutateAsync,
    isPending
  } = useMutation(t0);
  let t1;
  if ($[3] !== isPending || $[4] !== mutateAsync) {
    t1 = {
      verifyKyc: mutateAsync,
      isVerifying: isPending
    };
    $[3] = isPending;
    $[4] = mutateAsync;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}